import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../contexts/CurrencyContext';
import { Globe, ChevronDown } from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';

/**
 * Discreet currency switcher — sits in the header next to the language switcher.
 *
 * Renders as a small pill "€ EUR" that opens a dropdown of all supported currencies.
 */
export default function CurrencySwitcher() {
    const { t } = useTranslation();
    const { currency, currencies, setCurrency, isReady } = useCurrency();

    if (!isReady || !currencies?.length) return null;

    const current = currencies.find(c => c.code === currency) || currencies[0];

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    data-testid="currency-switcher"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm text-[#4B5563] hover:bg-[#F3F4F6] transition-colors"
                    aria-label={t('currency.switcher_aria', { defaultValue: 'Changer de devise' })}
                >
                    <Globe className="w-4 h-4" />
                    <span className="font-medium">{current.symbol}</span>
                    <span className="text-[#6B7280] uppercase tracking-wide text-xs hidden sm:inline">{current.code}</span>
                    <ChevronDown className="w-3 h-3" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
                {currencies.map(c => (
                    <DropdownMenuItem
                        key={c.code}
                        onSelect={() => setCurrency(c.code)}
                        data-testid={`currency-option-${c.code}`}
                        className={c.code === currency ? 'bg-[#EEF2FF] text-[#002FA7]' : ''}
                    >
                        <span className="w-8 font-semibold">{c.symbol}</span>
                        <span className="uppercase text-xs text-[#6B7280] mr-2">{c.code}</span>
                        <span className="flex-1">{c.name}</span>
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
